import { useLayoutEffect, useRef, useState, ReactNode } from 'react'
import styled, { css } from 'styled-components'
import Button from '../../general/Button/Button'
import Icon from '../../general/Icon'
import Text from '../../general/Typography/Text/Text'
import { useUiKitContext } from '../../utils/UiKitProvider/UiKitProvider'

export type CollapsibleTextProps = {
  children: ReactNode
  /**
   * @default 3
   */
  lines?: number
  /**
   * @default t('general.buttons.showMore')
   */
  showMoreLabel?: string
  /**
   * @default t('general.buttons.showLess')
   */
  showLessLabel?: string
  className?: string
  testId?: string
}

const CollapsibleText = (props: CollapsibleTextProps) => {
  const { translation: t } = useUiKitContext()
  const {
    children,
    lines = 3,
    showMoreLabel = t('general.buttons.showMore'),
    showLessLabel = t('general.buttons.showLess'),
    className,
    testId
  } = props
  const [isCollapsed, setIsCollapsed] = useState(true)
  const [isOverflowing, setIsOverflowing] = useState(false)
  const textRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const el = textRef.current
    if (!el || !isCollapsed) return

    const checkOverflow = () => {
      const overflows = el.scrollHeight > el.clientHeight
      setIsOverflowing(overflows)
    }

    const observer = new ResizeObserver(checkOverflow)
    observer.observe(el)

    return () => observer.disconnect()
  }, [children, lines, isCollapsed])

  const handleToggle = () => {
    setIsCollapsed((prev) => !prev)
  }

  return (
    <Container className={className} data-testid={testId}>
      <TextContent
        ref={textRef}
        $isCollapsed={isCollapsed}
        $lines={lines}
      >
        <Text>{children}</Text>
      </TextContent>
      {isOverflowing && (
        <ToggleButton
          variant="text"
          onClick={handleToggle}
          aria-expanded={!isCollapsed}
          data-testid={testId ? `${testId}-toggle` : undefined}
        >
          {isCollapsed ? showMoreLabel : showLessLabel}
          <ChevronIconWrapper>
            <Icon name={isCollapsed ? 'DownOutlined' : 'UpOutlined'} />
          </ChevronIconWrapper>
        </ToggleButton>
      )}
    </Container>
  )
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
`

const TextContent = styled.div<{
  $isCollapsed: boolean
  $lines: number
}>`
  ${({ $isCollapsed, $lines }) =>
    $isCollapsed &&
    css`
      display: -webkit-box;
      -webkit-line-clamp: ${$lines};
      -webkit-box-orient: vertical;
      overflow: hidden;
    `}
`

const ToggleButton = styled(Button)`
  margin-top: 8px;
  margin-left: -5px;
  gap: 4px;
  align-self: flex-start;
`

const ChevronIconWrapper = styled.span`
  display: inline-flex;
  transform: scaleY(0.65);
`

export default CollapsibleText
